import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { formatTL, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ProductImage from "../components/ProductImage";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  ScanLine,
  Search,
  Plus,
  Minus,
  Trash2,
  X,
  Wallet,
  Banknote,
  Split,
  ShoppingBag,
  DoorOpen,
  DoorClosed,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function uuid() {
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function POS() {
  const { user, isCashier } = useAuth();
  const nav = useNavigate();
  const [shift, setShift] = useState(null); // active shift (cashier only)
  const [shiftLoading, setShiftLoading] = useState(true);
  const [openShiftOpen, setOpenShiftOpen] = useState(false);
  const [closeShiftOpen, setCloseShiftOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [blindClose, setBlindClose] = useState(true);

  const [cart, setCart] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [payment, setPayment] = useState("nakit");
  const [cashAmount, setCashAmount] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [discountMode, setDiscountMode] = useState("tl");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [pinPromptOpen, setPinPromptOpen] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState(15);
  const clientRefRef = useRef(uuid());
  const wedgeBuf = useRef("");

  const refreshShift = () => {
    if (!isCashier) { setShiftLoading(false); return; }
    api.get("/shifts/current").then((r) => setShift(r.data.shift)).finally(() => setShiftLoading(false));
  };

  useEffect(() => {
    refreshShift();
    api.get("/settings").then((r) => {
      setMaxDiscount(Number(r.data.cashier_max_discount_pct || 15));
      setBlindClose(r.data.blind_close_enabled !== false);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [isCashier]);

  useEffect(() => {
    const onKey = (e) => {
      const tgt = e.target;
      const tag = (tgt?.tagName || "").toLowerCase();
      const inField = tag === "input" || tag === "textarea" || tgt?.isContentEditable;
      if (inField) return;
      if (e.key === "Enter") {
        const code = wedgeBuf.current.trim();
        wedgeBuf.current = "";
        if (code.length >= 3) { e.preventDefault(); handleBarcode(code); }
        return;
      }
      if (e.key.length === 1) {
        wedgeBuf.current += e.key;
        clearTimeout(onKey._t);
        onKey._t = setTimeout(() => (wedgeBuf.current = ""), 200);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((s, c) => s + Number(c.product.sale_price || 0) * c.quantity, 0);
    let discount = 0;
    const dv = Number(discountValue || 0);
    if (dv > 0) discount = discountMode === "pct" ? (subtotal * dv) / 100 : dv;
    if (discount > subtotal) discount = subtotal;
    const total = Math.max(0, subtotal - discount);
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      total: Math.round(total * 100) / 100,
      units: cart.reduce((s, c) => s + c.quantity, 0),
    };
  }, [cart, discountValue, discountMode]);

  const canComplete = cart.length > 0 && cartTotals.total >= 0 && !submitting;

  const addProductToCart = useCallback((product) => {
    setCart((prev) => {
      const existing = prev.find((p) => p.product.id === product.id);
      if (existing) {
        if (existing.quantity + 1 > product.stock) { toast.error(`Stok yetersiz (mevcut ${product.stock})`); return prev; }
        return prev.map((p) => (p.product.id === product.id ? { ...p, quantity: p.quantity + 1 } : p));
      }
      if (product.stock <= 0) { toast.error("Bu ürünün stoğu yok"); return prev; }
      return [...prev, { product, quantity: 1 }];
    });
  }, []);

  const handleBarcode = async (code) => {
    try {
      const { data } = await api.get("/products/lookup", { params: { code } });
      addProductToCart(data);
      toast.success(`${data.name} eklendi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bu barkoda ait ürün bulunamadı");
    }
  };

  const doSearch = async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try { const { data } = await api.get("/products", { params: { q } }); setSearchResults(data); } catch (e) { setSearchResults([]); }
  };

  const changeQty = (pid, delta) => {
    setCart((prev) => prev.map((c) => {
      if (c.product.id !== pid) return c;
      const nq = c.quantity + delta;
      if (nq <= 0) return null;
      if (nq > c.product.stock) { toast.error(`Stok yetersiz (max ${c.product.stock})`); return c; }
      return { ...c, quantity: nq };
    }).filter(Boolean));
  };

  const removeItem = (pid) => setCart((prev) => prev.filter((c) => c.product.id !== pid));
  const clearCart = () => { setCart([]); setDiscountValue(""); setCashAmount(""); setTransferAmount(""); setPayment("nakit"); };

  const openConfirm = () => {
    if (!cart.length) return toast.error("Sepet boş");
    if (isCashier && !shift) return toast.error("Aktif vardiyanız yok — önce vardiya açın");
    if (isCashier && cartTotals.subtotal > 0) {
      const pct = (cartTotals.discount / cartTotals.subtotal) * 100;
      if (pct > maxDiscount + 0.01) { setPinPromptOpen(true); return; }
    }
    if (payment === "karma") {
      const sum = Number(cashAmount || 0) + Number(transferAmount || 0);
      if (Math.abs(sum - cartTotals.total) > 0.01) { toast.error(`Nakit + Havale toplamı ${cartTotals.total.toFixed(2)} ₺ olmalı`); return; }
    }
    setConfirming(true);
  };

  const submitSale = async () => {
    setSubmitting(true);
    try {
      const sub = cartTotals.subtotal;
      const disc = cartTotals.discount;
      const items = cart.map((c) => {
        const lineGross = Number(c.product.sale_price || 0) * c.quantity;
        const lineDisc = sub > 0 ? (lineGross / sub) * disc : 0;
        return { product_id: c.product.id, quantity: c.quantity, discount: Math.round(lineDisc * 100) / 100 };
      });
      const totalDist = items.reduce((s, i) => s + i.discount, 0);
      const diff = Math.round((disc - totalDist) * 100) / 100;
      if (items.length && Math.abs(diff) > 0) items[items.length - 1].discount = Math.round((items[items.length - 1].discount + diff) * 100) / 100;

      const body = {
        client_ref: clientRefRef.current,
        items,
        payment_method: payment,
        cash_amount: payment === "nakit" ? cartTotals.total : payment === "karma" ? Number(cashAmount || 0) : 0,
        transfer_amount: payment === "havale" ? cartTotals.total : payment === "karma" ? Number(transferAmount || 0) : 0,
        admin_pin: adminPin || undefined,
      };
      const { data } = await api.post("/sales", body);
      toast.success(`Satış tamamlandı: ${data.sale_number}`);
      clientRefRef.current = uuid();
      setConfirming(false); setAdminPin(""); clearCart(); refreshShift();
      nav(`/satislar/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Satış tamamlanamadı";
      toast.error(detail);
      if (String(detail).includes("PIN")) { setPinPromptOpen(true); setConfirming(false); }
      if (String(detail).toLowerCase().includes("vardiya")) { setConfirming(false); refreshShift(); }
    } finally { setSubmitting(false); }
  };

  // Shift-gate: cashier without active shift sees only OpenShift screen
  if (isCashier && !shiftLoading && !shift) {
    return <OpenShiftScreen user={user} onOpened={(s) => { setShift(s); toast.success(`Vardiya açıldı: ${s.shift_number}`); }} />;
  }

  return (
    <div data-testid="pos-root" className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
            {isCashier ? "KASİYER · " + (user?.name || "") : "YÖNETİCİ"}
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">Yeni Satış</h1>
          {shift && (
            <div className="mt-2 text-xs font-mono-code text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1 inline-flex items-center gap-2">
              <DoorOpen className="w-3.5 h-3.5" /> Vardiya {shift.shift_number} · açılış {formatTL(shift.opening_cash)} · {shift.sales_count} satış
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="pos-scan" onClick={() => setScanning(true)} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl gold-gradient text-white text-sm font-medium">
            <ScanLine className="w-5 h-5" /> Barkod Tara
          </button>
          <button data-testid="pos-search" onClick={() => { setSearchOpen(true); setTimeout(() => document.getElementById("pos-search-input")?.focus(), 50); }} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-stone-300 text-stone-800 text-sm hover:bg-stone-50">
            <Search className="w-5 h-5" /> Ürün Ara
          </button>
          {shift && (
            <>
              <button data-testid="pos-shift-summary" onClick={() => setSummaryOpen(true)} className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-stone-300 text-stone-800 text-sm hover:bg-stone-50">
                <ClipboardList className="w-4 h-4" /> Vardiya Özeti
              </button>
              <button data-testid="pos-shift-close" onClick={() => setCloseShiftOpen(true)} className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-red-300 text-red-700 text-sm hover:bg-red-50">
                <DoorClosed className="w-4 h-4" /> Vardiyayı Kapat
              </button>
            </>
          )}
        </div>
      </div>

      {/* Cart */}
      <div className="card-lux overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-amber-700" />
            <span className="font-serif-display text-lg text-stone-900">Sepet</span>
            <span className="text-xs text-stone-500 font-mono-code">{cartTotals.units} adet</span>
          </div>
          {cart.length > 0 && <button data-testid="pos-clear" onClick={clearCart} className="text-xs text-red-700 hover:text-red-900">Sepeti Temizle</button>}
        </div>

        {cart.length === 0 ? (
          <div className="p-10 text-center text-stone-400"><ShoppingBag className="w-8 h-8 mx-auto mb-3 opacity-40" />Sepetiniz boş. Barkod tarayın veya ürün arayın.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {cart.map((c) => {
              const line = Number(c.product.sale_price || 0) * c.quantity;
              return (
                <li key={c.product.id} data-testid={`cart-row-${c.product.sku}`} className="flex items-center gap-3 p-3 sm:p-4">
                  <ProductImage path={c.product.image_path} size={56} alt={c.product.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{c.product.name}</div>
                    <div className="text-xs text-stone-500 truncate">{c.product.feature}</div>
                    <div className="text-xs font-mono-code text-amber-700 mt-0.5">{formatTL(c.product.sale_price)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`cart-dec-${c.product.sku}`} onClick={() => changeQty(c.product.id, -1)} className="w-9 h-9 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                    <div data-testid={`cart-qty-${c.product.sku}`} className="w-10 text-center font-mono-code text-sm">{c.quantity}</div>
                    <button data-testid={`cart-inc-${c.product.sku}`} onClick={() => changeQty(c.product.id, 1)} className="w-9 h-9 rounded-lg border border-stone-200 hover:bg-stone-50 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                  </div>
                  <div className="w-24 text-right font-mono-code text-sm text-stone-900">{formatTL(line)}</div>
                  <button data-testid={`cart-remove-${c.product.sku}`} onClick={() => removeItem(c.product.id)} className="p-2 rounded-md text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="px-5 py-4 border-t border-stone-200 bg-stone-50/40 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Ara Toplam</div>
              <div className="font-mono-code text-lg">{formatTL(cartTotals.subtotal)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">İndirim {isCashier ? `(max %${maxDiscount})` : ""}</div>
              <div className="flex gap-1">
                <input data-testid="pos-discount-value" type="number" min="0" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder="0" className="w-full px-2 py-2 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400" />
                <select data-testid="pos-discount-mode" value={discountMode} onChange={(e) => setDiscountMode(e.target.value)} className="px-2 py-2 border border-stone-200 rounded-lg text-sm bg-white"><option value="tl">₺</option><option value="pct">%</option></select>
              </div>
              <div className="mt-1 text-xs text-stone-500 font-mono-code">-{formatTL(cartTotals.discount)}</div>
            </div>
            <div className="sm:text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Genel Toplam</div>
              <div data-testid="pos-total" className="font-serif-display text-3xl text-amber-800">{formatTL(cartTotals.total)}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[{ k: "nakit", label: "Nakit", icon: Banknote }, { k: "havale", label: "Havale", icon: Wallet }, { k: "karma", label: "Karma", icon: Split }].map((p) => (
              <button key={p.k} data-testid={`pay-${p.k}`} onClick={() => setPayment(p.k)} className={`inline-flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium ${payment === p.k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 hover:bg-stone-50 text-stone-700"}`}>
                <p.icon className="w-4 h-4" />{p.label}
              </button>
            ))}
          </div>

          {payment === "karma" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Nakit Tutarı (₺)</div><input data-testid="karma-cash" type="number" step="0.01" min="0" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 font-mono-code" /></label>
              <label className="block"><div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Havale Tutarı (₺)</div><input data-testid="karma-transfer" type="number" step="0.01" min="0" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 font-mono-code" /></label>
              <div className="col-span-2 text-xs text-stone-500 font-mono-code">Toplam: {formatTL(Number(cashAmount || 0) + Number(transferAmount || 0))} / Beklenen: {formatTL(cartTotals.total)}</div>
            </div>
          )}

          <button data-testid="pos-complete" disabled={!canComplete} onClick={openConfirm} className="w-full py-4 rounded-xl gold-gradient text-white text-base font-semibold tracking-wide disabled:opacity-50">
            SATIŞI TAMAMLA · {formatTL(cartTotals.total)}
          </button>
        </div>
      </div>

      {scanning && <BarcodeScanner onDetected={async (code) => { await handleBarcode(code); setScanning(false); }} onClose={() => setScanning(false)} />}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl border border-stone-200 mt-8 overflow-hidden">
            <div className="flex items-center gap-3 p-4 border-b border-stone-200">
              <Search className="w-4 h-4 text-stone-400" />
              <input id="pos-search-input" data-testid="pos-search-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); doSearch(e.target.value); }} placeholder="Ürün adı, kod, barkod..." className="flex-1 outline-none text-sm" autoFocus />
              <button onClick={() => setSearchOpen(false)} className="p-1 rounded-md hover:bg-stone-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-stone-100">
              {searchResults.length === 0 && <div className="text-center py-8 text-stone-400 text-sm">Sonuç yok</div>}
              {searchResults.map((p) => (
                <button key={p.id} data-testid={`search-result-${p.sku}`} onClick={() => { addProductToCart(p); setSearchOpen(false); setSearchTerm(""); setSearchResults([]); }} className="w-full flex items-center gap-3 p-3 hover:bg-amber-50/40 text-left">
                  <ProductImage path={p.image_path} size={44} alt={p.name} />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{p.name}</div><div className="text-xs text-stone-500 truncate">{p.feature} · {p.category}</div></div>
                  <div className="text-right"><div className="text-sm font-mono-code text-amber-700">{formatTL(p.sale_price)}</div><div className="text-[11px] text-stone-500 font-mono-code">Stok: {p.stock}</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-6">
            <div className="font-serif-display text-2xl text-stone-900 mb-2">Satışı Tamamla</div>
            <div className="text-sm text-stone-500 mb-4">Aşağıdaki bilgileri onayladığınızda satış kaydedilir.</div>
            <div className="space-y-2 text-sm">
              <Row label="Ürün Adedi" value={cartTotals.units} />
              <Row label="Ara Toplam" value={formatTL(cartTotals.subtotal)} />
              <Row label="İndirim" value={`-${formatTL(cartTotals.discount)}`} />
              <div className="h-px bg-stone-200 my-1" />
              <Row label="Genel Toplam" value={<span className="font-serif-display text-xl text-amber-800">{formatTL(cartTotals.total)}</span>} />
              <Row label="Ödeme" value={payment === "karma" ? `Karma (N: ${formatTL(cashAmount || 0)} · H: ${formatTL(transferAmount || 0)})` : payment.toUpperCase()} />
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => setConfirming(false)} className="flex-1 px-4 py-3 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm" disabled={submitting}>Vazgeç</button>
              <button data-testid="pos-confirm-submit" onClick={submitSale} disabled={submitting} className="flex-1 px-4 py-3 rounded-lg gold-gradient text-white text-sm font-medium disabled:opacity-60">{submitting ? "Kaydediliyor…" : "Onayla ve Kaydet"}</button>
            </div>
          </div>
        </div>
      )}

      {pinPromptOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm border border-stone-200 p-6">
            <div className="font-serif-display text-2xl text-stone-900 mb-2">Yönetici PIN</div>
            <div className="text-sm text-stone-500 mb-4">İndirim yetkinizin üzerinde. Yönetici PIN'i girin.</div>
            <input data-testid="pos-admin-pin" type="password" value={adminPin} onChange={(e) => setAdminPin(e.target.value)} placeholder="****" className="w-full px-3 py-3 border border-stone-200 rounded-lg outline-none focus:border-amber-400 font-mono-code text-center text-lg mb-4" />
            <div className="flex gap-2">
              <button onClick={() => { setPinPromptOpen(false); setAdminPin(""); }} className="flex-1 px-4 py-3 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
              <button data-testid="pos-pin-ok" onClick={() => { setPinPromptOpen(false); setConfirming(true); }} disabled={!adminPin} className="flex-1 px-4 py-3 rounded-lg gold-gradient text-white text-sm font-medium disabled:opacity-60">Onayla</button>
            </div>
          </div>
        </div>
      )}

      {closeShiftOpen && shift && (
        <CloseShiftModal shift={shift} blindClose={blindClose} hasCart={cart.length > 0} onClose={() => setCloseShiftOpen(false)} onDone={() => { setCloseShiftOpen(false); setShift(null); toast.success("Vardiya kapatıldı"); nav("/satislar"); }} />
      )}
      {summaryOpen && shift && (
        <ShiftSummaryModal shiftId={shift.id} onClose={() => setSummaryOpen(false)} />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><div className="text-stone-500">{label}</div><div className="text-stone-900">{value}</div></div>;
}

function OpenShiftScreen({ user, onOpened }) {
  const [cash, setCash] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (cash === "" || Number(cash) < 0) return toast.error("Başlangıç nakdi giriniz");
    setSaving(true);
    try {
      const { data } = await api.post("/shifts", { opening_cash: Number(cash), opening_note: note });
      onOpened(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vardiya açılamadı");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="open-shift-root" className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">RIVACO</div>
        <h1 className="font-serif-display text-3xl mt-1">Merhaba {user?.name || user?.username}</h1>
        <p className="text-stone-500 text-sm mt-1">Satışa başlamak için vardiyanızı açın.</p>
      </div>
      <form onSubmit={submit} className="card-lux p-6 space-y-4">
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-1 font-semibold">Başlangıç Kasa Nakdi (₺)</div>
          <input data-testid="open-shift-cash" type="number" min="0" step="0.01" value={cash} onChange={(e) => setCash(e.target.value)} className="w-full px-4 py-3 border border-stone-200 rounded-lg outline-none focus:border-amber-400 text-2xl font-mono-code text-center" placeholder="0,00" autoFocus required />
        </label>
        <label className="block">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-1 font-semibold">Not (opsiyonel)</div>
          <input data-testid="open-shift-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400" />
        </label>
        <button data-testid="open-shift-submit" type="submit" disabled={saving} className="w-full py-4 rounded-xl gold-gradient text-white font-semibold disabled:opacity-60">
          {saving ? "Açılıyor…" : "VARDİYAYI BAŞLAT"}
        </button>
      </form>
    </div>
  );
}

function CloseShiftModal({ shift, blindClose, hasCart, onClose, onDone }) {
  const [counted, setCounted] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // {expected, diff}
  const [confirmed, setConfirmed] = useState(!blindClose); // when blindClose is false, show expected upfront

  const revealPreview = async () => {
    if (counted === "" || Number(counted) < 0) return toast.error("Sayılan nakdi giriniz");
    try {
      const { data } = await api.get(`/shifts/${shift.id}`);
      const expected = data.expected_cash;
      const diff = Math.round((Number(counted) - expected) * 100) / 100;
      setPreview({ expected, diff });
      setConfirmed(true);
    } catch (e) {
      toast.error("Özet alınamadı");
    }
  };

  const submit = async () => {
    if (counted === "" || Number(counted) < 0) return toast.error("Sayılan nakdi giriniz");
    setSaving(true);
    try {
      await api.post(`/shifts/${shift.id}/close`, { counted_cash: Number(counted), cashier_note: note });
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kapatma başarısız");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-5 max-h-[90vh] overflow-y-auto">
        <div className="mb-3">
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">VARDİYA {shift.shift_number}</div>
          <div className="font-serif-display text-2xl">Vardiyayı Kapat</div>
        </div>
        {hasCart && <div className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">Uyarı: Tamamlanmamış satış sepetiniz var.</div>}
        <label className="block mb-3">
          <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Kasada Saydığınız Fiziksel Nakit (₺)</div>
          <input data-testid="close-counted" type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} className="w-full px-4 py-3 border border-stone-200 rounded-lg outline-none focus:border-amber-400 text-2xl font-mono-code text-center" placeholder="0,00" autoFocus />
        </label>

        {blindClose && !confirmed && (
          <button data-testid="close-reveal" onClick={revealPreview} className="w-full mb-3 py-3 rounded-lg border border-stone-300 text-stone-800 text-sm hover:bg-stone-50">
            Sayımı Onayla ve Sonucu Gör
          </button>
        )}

        {confirmed && (
          <div className="space-y-2 text-sm mb-3">
            <div className="flex justify-between"><span className="text-stone-500">Beklenen Kasa</span><span className="font-mono-code">{formatTL(preview?.expected ?? shift.expected_cash)}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Sayılan Kasa</span><span className="font-mono-code">{formatTL(Number(counted || 0))}</span></div>
            <div className="h-px bg-stone-200" />
            <div className="flex justify-between font-semibold">
              <span>Fark</span>
              <span className={`font-mono-code ${(preview?.diff ?? 0) === 0 ? "text-stone-800" : (preview?.diff ?? 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                {(preview?.diff ?? 0) === 0 ? "0,00 ₺ (Tam)" : (preview?.diff ?? 0) < 0 ? `${formatTL(preview?.diff ?? 0)} (Eksik)` : `+${formatTL(preview?.diff ?? 0)} (Fazla)`}
              </span>
            </div>
          </div>
        )}

        {confirmed && (preview?.diff ?? 0) !== 0 && (
          <label className="block mb-3">
            <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Kasa Farkı Açıklaması (zorunlu)</div>
            <textarea data-testid="close-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400" />
          </label>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} disabled={saving} className="flex-1 px-4 py-3 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
          <button data-testid="close-submit" onClick={submit} disabled={saving || (blindClose && !confirmed)} className="flex-1 px-4 py-3 rounded-lg gold-gradient text-white text-sm font-medium disabled:opacity-60">
            {saving ? "Kapatılıyor…" : "Vardiyayı Kapat"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShiftSummaryModal({ shiftId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/shifts/${shiftId}`).then((r) => setData(r.data)); }, [shiftId]);
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">{data.shift_number}</div>
            <div className="font-serif-display text-2xl">Vardiya Özeti</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1 text-sm">
          <SumRow label="Satış Sayısı" value={data.sales_count} />
          <SumRow label="Satılan Adet" value={data.units_sold} />
          <SumRow label="Brüt" value={formatTL(data.gross)} />
          <SumRow label="İndirim" value={`-${formatTL(data.discount_total)}`} />
          <SumRow label="Net Satış" value={<span className="font-semibold">{formatTL(data.net)}</span>} />
          <SumRow label="Nakit" value={formatTL(data.cash_sales)} />
          <SumRow label="Havale" value={formatTL(data.transfer_sales)} />
        </div>
      </div>
    </div>
  );
}
function SumRow({ label, value }) {
  return <div className="flex justify-between"><span className="text-stone-500">{label}</span><span className="font-mono-code">{value}</span></div>;
}
